import { useEffect, useMemo } from 'react';
import { DCSCacheType } from 'src/hooks/useCachedDCS';
import { useCachedDCS } from 'src/services/dcs/CachedDCS';
import { transformBundleForTemplate, transformProductForTemplate } from 'src/utils/DcsTemplateTransformUtils';
import { parseDCSTemplateText } from 'src/utils/ParseDCSTemplateText';

export enum DCSObjectType {
  PRODUCTS = 'products',
  BUNDLES = 'bundles',
}
/**
 * Accepts an input string that may contain DCS templatee expressions inside and returns processed string with the resolved value of the DCS expressions
 * @param str: input string
 * @returns if string has DCS expressions inside, returns the parsed string with resolved value of the DCS expressions. returns input string as is otherwise.
 * @example
 * const dcsText = useDCSText('lorem ${{bundles.HOME_REPAIR_BETTER.pricing.msrp}}')
 * // dcsText: '' (until DCS data is loading)
 * // dcsText: 'lorem $1,694' (after DCS data is retrieved)
 *
 * const dcsText = useDCSText('lorem ipsum')
 * // dcsText: 'lorem ipsum'
 */
export const useDCSText = (str: string): string | undefined => {
  const { bundlesMap, productsMap, isSellerAvailable, fetchBundles, fetchProducts } = useCachedDCS();

  const dcsObjects = useMemo(() => parseDCSTemplateText(str), [str]);

  const { bundleIds, productIds } = useMemo(() => {
    if (!dcsObjects) return {};
    const filterDCSObjects = (objectType: DCSObjectType, isForceUpdate: boolean) => {
      return dcsObjects
        .filter(item => item.dcsObjectType === objectType && item.isForceUpdate === isForceUpdate)
        .map(item => item.dcsID);
    };
    return {
      bundleIds: {
        withoutForceUpdate: filterDCSObjects(DCSObjectType.BUNDLES, false),
        withForceUpdate: filterDCSObjects(DCSObjectType.BUNDLES, true),
      },
      productIds: {
        withoutForceUpdate: filterDCSObjects(DCSObjectType.PRODUCTS, false),
        withForceUpdate: filterDCSObjects(DCSObjectType.PRODUCTS, true),
      },
    };
  }, [dcsObjects]);

  useEffect(() => {
    if (!isSellerAvailable) return;
    if (bundleIds?.withoutForceUpdate.length) fetchBundles(bundleIds.withoutForceUpdate);
    if (bundleIds?.withForceUpdate.length) fetchBundles(bundleIds.withForceUpdate, DCSCacheType.TEMPORARY);
    if (productIds?.withoutForceUpdate.length) fetchProducts(productIds.withoutForceUpdate);
    if (productIds?.withForceUpdate.length) fetchProducts(productIds.withForceUpdate, DCSCacheType.TEMPORARY);
  }, [isSellerAvailable, bundleIds, productIds, fetchBundles, fetchProducts]);

  const loading = useMemo(() => {
    if (!dcsObjects) return true;
    for (const dcsObj of dcsObjects) {
      const { dcsObjectType, dcsID } = dcsObj;
      if (dcsObjectType === DCSObjectType.BUNDLES && !bundlesMap[dcsID]) return true;
      if (dcsObjectType === DCSObjectType.PRODUCTS && !productsMap[dcsID]) return true;
    }
    return false;
  }, [dcsObjects, bundlesMap, productsMap]);

  const transformedBundlesMap = useMemo(() => Object.keys(bundlesMap).reduce((transformedMap, key) => {
    transformedMap[key] = transformBundleForTemplate(bundlesMap[key]);
    return transformedMap;
  }, {}), [bundlesMap]);

  const transformedProductsMap = useMemo(() => Object.keys(productsMap).reduce((transformedMap, key) => {
    transformedMap[key] = transformProductForTemplate(productsMap[key]);
    return transformedMap;
  }, {}), [productsMap]);

  const dcsTextTemplateData = useMemo(() => {
    if (!dcsObjects) return {};
    return dcsObjects.reduce((templateData, currDcsObj) => {
      const { dcsObjectType, dcsID, dcsObjectPath, matchedValue } = currDcsObj;
      let currentVal = null;
      switch (dcsObjectType) {
        case DCSObjectType.BUNDLES:
          currentVal = transformedBundlesMap[dcsID];
          break;
        case DCSObjectType.PRODUCTS:
          currentVal = transformedProductsMap[dcsID];
          break;
        default:
          break;
      }
      if (!currentVal) return templateData;
      const paths = dcsObjectPath.split('.');
      for (const currentPath of paths) {
        if (!currentPath || typeof currentVal !== 'object') return templateData;
        currentVal = currentVal[currentPath];
      }
      templateData[matchedValue] = currentVal;
      return templateData;
    }, {});
  }, [dcsObjects, transformedBundlesMap, transformedProductsMap]);

  const dcsText = useMemo(() => {
    if (!dcsObjects || !str || loading || Object.values(dcsTextTemplateData).some(item => item === undefined)) return '';
    let dcsText = str;
    for (const dcsObj of dcsObjects) {
      const { matchedValue } = dcsObj;
      dcsText = dcsText.replace(matchedValue, dcsTextTemplateData[matchedValue]);
    }
    return dcsText;
  }, [loading, str, dcsObjects, dcsTextTemplateData]);

  return dcsObjects ? dcsText : str;
};
